import {useMemo} from 'react';
import {ISharedMessageMetadata} from '../dto/MessageSharing.dto';

/**
 * Return type for the useSharedMessageState hook.
 * Provides computed flags and metadata for shared message rendering logic.
 */
export interface SharedMessageState {
  /** Whether this message is shared from a district parent */
  isShared: boolean;
  /** Whether this message came from a district (shared_from_district) */
  isSharedFromDistrict: boolean;
  /** Whether the message is editable (false for child company users on shared messages) */
  isEditable: boolean;
  /** Whether mutation actions (edit, delete, retarget, schedule) should be disabled */
  isReadOnly: boolean;
  /** The owner company name to display (e.g. "Springfield District") */
  ownerCompanyName: string;
  /** The visibility origin value (e.g. "district_parent") */
  visibilityOrigin: string;
  /** The raw shared metadata object, or undefined if not shared */
  sharedMetadata: ISharedMessageMetadata | undefined;
}

/**
 * Hook to determine shared message state for detail views.
 *
 * Usage:
 * ```tsx
 * const { isReadOnly, isShared, ownerCompanyName } = useSharedMessageState(data?.shared_metadata);
 * ```
 *
 * When `shared_metadata` is present and `editable` is false:
 * - Edit, delete, retarget, and schedule actions should be disabled
 * - Share management controls should not be rendered for child company users
 * - The SharedMessageBadge should be displayed with the owner company name
 * - Audio/track preview remains available
 *
 * @param sharedMetadata - The `shared_metadata` field from the API response (optional)
 */
export const useSharedMessageState = (
  sharedMetadata?: ISharedMessageMetadata | null,
): SharedMessageState => {
  return useMemo(() => {
    if (!sharedMetadata) {
      return {
        isShared: false,
        isSharedFromDistrict: false,
        isEditable: true,
        isReadOnly: false,
        ownerCompanyName: '',
        visibilityOrigin: '',
        sharedMetadata: undefined,
      };
    }

    const isShared = sharedMetadata.is_shared === true;
    const isSharedFromDistrict = sharedMetadata.shared_from_district === true;
    const isEditable = sharedMetadata.editable !== false;
    const isReadOnly = !isEditable;

    return {
      isShared,
      isSharedFromDistrict,
      isEditable,
      isReadOnly,
      ownerCompanyName: sharedMetadata.owner_company_name || '',
      visibilityOrigin: sharedMetadata.visibility_origin || '',
      sharedMetadata,
    };
  }, [sharedMetadata]);
};
